<template>
  <div class="bar-metric">
    <div class="bar-metric__header">
      <div class="bar-metric__label">
        {{ label }}
        <span v-if="description" :id="tooltipId" class="bar-metric__info-icon">
          <font-awesome-icon :icon="['fas', 'info-circle']" />
        </span>
        <b-tooltip v-if="description" :target="tooltipId" placement="top">
          {{ description }}
        </b-tooltip>
      </div>
      <span class="bar-metric__delta bar-metric__delta--mobile" :class="deltaColorClass">{{ deltaFormatted }}</span>
    </div>

    <div class="bar-metric__body">
      <div class="bar-metric__label bar-metric__label--desktop">
        {{ label }}
        <span v-if="description" :id="`${tooltipId}-desktop`" class="bar-metric__info-icon">
          <font-awesome-icon :icon="['fas', 'info-circle']" />
        </span>
        <b-tooltip v-if="description" :target="`${tooltipId}-desktop`" placement="top">
          {{ description }}
        </b-tooltip>
      </div>
      <div class="bar-metric__bar-area">
        <div class="bar-metric__bar-container">
          <div class="bar-metric__track">
            <div class="bar-metric__fill" :style="{ width: fillPercent + '%' }" />
          </div>
          <span
            class="bar-metric__fill-label"
            :class="fillLabelInside ? 'bar-metric__fill-label--inside' : 'bar-metric__fill-label--outside'"
            :style="{ left: fillPercent + '%' }"
            >{{ formattedValue }}</span
          >
          <div class="bar-metric__value-tag">{{ valueLabel }}</div>
          <div v-if="averagePercent !== null" class="bar-metric__avg-marker" :style="{ left: averagePercent + '%' }">
            <div class="bar-metric__avg-line" />
            <div class="bar-metric__avg-tag" :class="avgTagAlignClass">{{ averageLabel }}: {{ formattedAverage }}</div>
          </div>
        </div>
      </div>
      <span class="bar-metric__delta bar-metric__delta--desktop" :class="deltaColorClass">{{ deltaFormatted }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  description: { type: String, default: null },
  tooltipId: { type: String, default: null },
  value: { type: Number, default: null },
  average: { type: Number, default: null },
  maxValue: { type: Number, default: 100 },
  formattedValue: { type: String, default: '—' },
  formattedAverage: { type: String, default: '—' },
  deltaFormatted: { type: String, default: '—' },
  deltaColorClass: { type: String, default: 'delta-neutral' },
  valueLabel: { type: String, default: 'Your company' },
  averageLabel: { type: String, default: 'Sector avg' },
});

const fillPercent = computed(() => {
  if (props.value === null || props.maxValue <= 0) {
    return 0;
  }
  return Math.max(0, Math.min((props.value / props.maxValue) * 100, 100));
});

const averagePercent = computed(() => {
  if (props.average === null || props.maxValue <= 0) {
    return null;
  }
  return Math.max(0, Math.min((props.average / props.maxValue) * 100, 100));
});

const fillLabelInside = computed(() => fillPercent.value > 80);

const avgTagAlignClass = computed(() => {
  if (averagePercent.value === null) {
    return '';
  }
  return averagePercent.value > 70 ? 'bar-metric__avg-tag--left' : '';
});
</script>

<style lang="scss" scoped>
.bar-metric {
  padding: 0.6rem 0;

  &__header {
    display: none;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.35rem;

    @media (max-width: 767px) {
      display: flex;
    }
  }

  &__body {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
  }

  &__label {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--palette-color-default);

    &--desktop {
      flex: 0 0 10rem;
      padding-top: 0.25rem;

      @media (max-width: 767px) {
        display: none;
      }
    }
  }

  &__info-icon {
    margin-left: 0.35rem;
    color: var(--palette-color-default-lighten-40);
    cursor: help;
    font-size: 0.85rem;
  }

  &__bar-area {
    flex: 1;
    min-width: 0;
  }

  &__delta {
    font-weight: 700;
    font-size: 1rem;
    white-space: nowrap;
    padding: 0.15rem 0.5rem;
    border-radius: 0.25rem;

    &--desktop {
      flex: 0 0 10rem;
      text-align: center;

      @media (max-width: 767px) {
        display: none;
      }
    }

    &--mobile {
      display: none;

      @media (max-width: 767px) {
        display: inline;
      }
    }
  }

  &__bar-container {
    position: relative;
    margin-bottom: 1.25rem;
  }

  &__track {
    position: relative;
    height: 1.75rem;
    background-color: var(--palette-color-default-lighten-90);
    border-radius: 0.375rem;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background-color: var(--palette-color-brand-primary);
    border-radius: 0.375rem;
    transition: width 0.5s ease;
    min-width: 2px;
  }

  &__fill-label {
    position: absolute;
    top: 50%;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
    pointer-events: none;

    &--outside {
      transform: translate(0.4rem, -50%);
      color: var(--palette-color-brand-primary);
    }

    &--inside {
      transform: translate(calc(-100% - 0.4rem), -50%);
      color: var(--palette-color-white);
    }
  }

  &__avg-marker {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
  }

  &__avg-line {
    position: absolute;
    top: -2px;
    width: 0;
    height: calc(1.75rem + 4px);
    border-left: 2px dashed var(--palette-color-default-lighten-20);
  }

  &__value-tag {
    position: absolute;
    top: calc(1.75rem + 4px);
    left: 0;
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--palette-color-default-lighten-20);
  }

  &__avg-tag {
    position: absolute;
    top: calc(1.75rem + 4px);
    left: 0;
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--palette-color-default-lighten-20);

    &--left {
      left: auto;
      right: 0;
    }
  }
}

.delta-positive {
  color: var(--palette-color-success);
}

.delta-neutral {
  color: var(--palette-color-default-lighten-20);
}
</style>
